import { compose } from 'redux';
import { noop, keyBy } from 'lodash/fp';
import { reset } from 'redux-form';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText, useAPI } from '@mews/framework';
import { WithScope, WithServiceId } from '@mews-commander/types';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import {
    commanderForm,
    dateString,
    DateTimePickerField,
    InputField,
    integerGreaterOrEqualToOne,
    presence,
    SelectField,
    timeString,
    validation,
} from '@mews-commander/legacy-form';
import { getServiceDefaultAgeCategoryId } from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { fetchServiceProductPrices } from '@mews-commander/legacy-entities/src/actions/products';
import { isCurrentEnterpriseInMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { FullWidthFieldsContainer } from '@mews-commander/ui';
import { getProductOptions, hideConsumedField as hideConsumedFieldSelector } from '../selectors';
import { submitAddProductForm } from '../actions';
import {
    getMaximalSelectedEndUtc,
    getMinimalSelectedStartUtc,
} from '../selectors/reservationModuleState/reservationSelection';

const constraints = {
    productId: { presence },
    count: {
        presence,
        numericality: integerGreaterOrEqualToOne,
    },
    date: { presence, dateString },
    time: { presence, timeString },
};
interface Props extends WithScope, WithServiceId {
    reservationIds: string[];
    form: string;
    products: $TSFixMe[];
}
const AddProductForm = ({ products, form, serviceId, scope }: Props) => {
    const dispatch = useCommanderThunkDispatch();

    const hideConsumedField = useCommanderSelector((state) => hideConsumedFieldSelector(state, { form }));
    const startUtc = useCommanderSelector((state) => getMinimalSelectedStartUtc(state, { scope }));
    const endUtc = useCommanderSelector((state) => getMaximalSelectedEndUtc(state, { scope }));
    const isMultiTaxEnvironment = useCommanderSelector(isCurrentEnterpriseInMultiTaxEnvironment);
    const defaultCategoryId = useCommanderSelector((state) => getServiceDefaultAgeCategoryId(state, { serviceId }));

    const getServiceProductPrices = useCallback(async () => {
        if (startUtc && endUtc && defaultCategoryId) {
            return dispatch(
                fetchServiceProductPrices({
                    serviceId,
                    startUtc,
                    endUtc,
                    consumedUtc: startUtc,
                    occupancyData: [{ ageCategoryId: defaultCategoryId, personCount: 1 }],
                })
            );
        }
        return Promise.resolve({ value: [] });
    }, [dispatch, serviceId, startUtc, endUtc, defaultCategoryId]);

    const { data } = useAPI(getServiceProductPrices, []);
    const prices = keyBy('productId', data);
    const options = getProductOptions(isMultiTaxEnvironment, prices)(products);
    return (
        <FullWidthFieldsContainer>
            <SelectField {...productProps} options={options} />
            <InputField {...countProps} />
            {!hideConsumedField && <DateTimePickerField form={form} {...consumedProps} />}
        </FullWidthFieldsContainer>
    );
};
const productProps = {
    label: <LocalizedText textKey={TextKey.Product} />,
    name: 'productId',
};
const countProps = {
    label: <LocalizedText textKey={TextKey.Count} />,
    name: 'count',
};
const consumedProps = {
    label: <LocalizedText textKey={TextKey.Consumed} />,
    required: true,
};
export default compose(
    connect(
        undefined,
        // @ts-expect-error $TSFixMeError: Property 'reservationIds' does not exist on type '{}'.
        (dispatch, { reservationIds, form, onSuccess = noop }) => ({
            onSubmit: (values: $TSFixMe) =>
                // @ts-expect-error $TSFixMeError: Argument of type '(dispatch: $TSFixMe, getState: $TSFixMe, { container }: $TSFixMe) => any' is not assignable to paramet...
                (dispatch(submitAddProductForm(values, reservationIds)) as $TSFixMe).then(() => {
                    dispatch(reset(form));
                    onSuccess();
                }),
        })
    ),
    commanderForm({
        enableReinitialize: true,
        submitLabel: <LocalizedText textKey={TextKey.Add} />,
        validate: validation(constraints),
    })
)(AddProductForm);
